// SVG trend chart — mirrors the legacy TrendChart function.
// 14-day findings trend with area fill + points + date labels.

import type { TrendPoint } from "@/types/dashboard";

interface TrendChartProps {
  data: TrendPoint[];
}

export function TrendChart({ data }: TrendChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="h-[100px] flex items-center justify-center text-dim font-mono text-[11px]">
        No data
      </div>
    );
  }

  const max = Math.max(...data.map((d) => d.count), 1);
  const w = 720;
  const hg = 120;
  const pad = 20;
  const xStep = (w - pad * 2) / Math.max(data.length - 1, 1);

  const points = data.map((d, i) => {
    const x = pad + i * xStep;
    const y = hg - pad - (d.count / max) * (hg - pad * 2);
    return [x, y] as const;
  });

  const path = "M " + points.map((p) => p.join(",")).join(" L ");
  const last = points[points.length - 1];
  const areaPath = `${path} L ${last[0]},${hg - pad} L ${pad},${hg - pad} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${hg}`} className="w-full h-[120px]">
      <defs>
        <linearGradient id="trend-grad" x1="0" y1="0" x2="0" y2="1">
          <stop
            offset="0%"
            style={{ stopColor: "rgb(var(--color-accent))" }}
            stopOpacity="0.3"
          />
          <stop
            offset="100%"
            style={{ stopColor: "rgb(var(--color-accent))" }}
            stopOpacity="0"
          />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#trend-grad)" />
      <path
        d={path}
        fill="none"
        style={{ stroke: "rgb(var(--color-accent))" }}
        strokeWidth="2"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p[0]}
          cy={p[1]}
          r="3"
          style={{ fill: "rgb(var(--color-accent))" }}
        />
      ))}
      {data.map(
        (d, i) =>
          i % 2 === 0 && (
            <text
              key={`l${i}`}
              x={pad + i * xStep}
              y={hg - 4}
              style={{ fill: "rgb(var(--color-dim))" }}
              fontSize="9"
              textAnchor="middle"
              fontFamily="monospace"
            >
              {d.date.substring(5)}
            </text>
          ),
      )}
    </svg>
  );
}
